// Dónde dibujar un panel que se abre HACIA ARRIBA desde un botón, cuando el panel está
// portaleado al body — ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean.
//
// Los menús del composer (Configuración, Añadir, Tipo de video) vivían `absolute` respecto
// de su botón, y por eso la fila que los contiene tenía PROHIBIDO el `overflow-*`: cualquier
// scroll los recortaba. Desde que esa fila scrollea, los tres se portalean y se posicionan
// en coordenadas de viewport, que es lo que se calcula acá.
//
// Portal y no `position: fixed` a secas: el sheet que los contiene se anima con `translate`,
// y un ancestro con transform es el bloque contenedor de sus descendientes `fixed`
// (ADR-2026-08-18-la-animacion-de-entrada-no-deja-transform).

import { useEffect, useState, type RefObject } from 'react';

/** Aire entre el botón y el panel, en px. */
export const AIRE_SOBRE_EL_BOTON = 8;

export interface CoordenadasDePanel {
  /** `left` en coordenadas de viewport, ya acotado para no salirse de la pantalla. */
  left: number;
  /** `bottom` en coordenadas de viewport: el panel crece hacia arriba desde el botón. */
  bottom: number;
}

/**
 * Coordenadas del panel a partir del rectángulo del botón. Pura.
 *
 * `bottom` y no `top` porque el panel crece hacia arriba y su alto no se conoce (la lista
 * puede tener dos ítems o veinte). El `left` se acota a la ventana: un botón pegado al
 * borde derecho dejaba el panel medio afuera.
 */
export function coordenadasSobre(
  rect: { left: number; top: number },
  ventana: { ancho: number; alto: number },
  anchoPanel: number,
  aire: number = AIRE_SOBRE_EL_BOTON,
): CoordenadasDePanel {
  const maximo = Math.max(aire, ventana.ancho - anchoPanel - aire);
  return {
    left: Math.min(Math.max(rect.left, aire), maximo),
    bottom: ventana.alto - rect.top + aire,
  };
}

/**
 * Mide el botón mientras el panel está abierto. Devuelve `null` hasta la primera medición,
 * para que el panel no parpadee en la esquina antes de saber dónde va.
 */
export function usePosicionSobreElBoton(
  ref: RefObject<HTMLElement | null>,
  abierto: boolean,
  anchoPanel: number,
): CoordenadasDePanel | null {
  const [pos, setPos] = useState<CoordenadasDePanel | null>(null);
  useEffect(() => {
    if (!abierto) {
      setPos(null);
      return;
    }
    const medir = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r) {
        setPos(
          coordenadasSobre(
            r,
            { ancho: window.innerWidth, alto: window.innerHeight },
            anchoPanel,
          ),
        );
      }
    };
    medir();
    window.addEventListener('resize', medir);
    // `capture`: la fila del composer scrollea en horizontal, y el botón se mueve con ella.
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto, anchoPanel, ref]);
  return pos;
}
